import { Box } from '@approvalmax/ui/src/components';
import { arrayHelpers, compareHelpers } from '@approvalmax/utils';
import { useSetAtom } from 'jotai';
import { memo, useEffect, useMemo } from 'react';
import { useGetRequestVersions } from 'shared/data/webApp/v2';
import { contentSplitViewState } from 'shared/states';

import { EmptyVersionHistoryList, VersionHistoryItem } from './components';
import { Loading } from './RequestVersionHistory.loading';
import { messages } from './RequestVersionHistory.messages';
import { RequestVersionHistoryProps } from './RequestVersionHistory.types';

export const RequestVersionHistory = memo<RequestVersionHistoryProps>((props) => {
    const { requestId, companyId, selectedVersion, isDextRequest, onSelectVersion, setSplitViewFirstItemTitle } = props;

    const setContentSplitView = useSetAtom(contentSplitViewState);

    const { data: requestVersions, isLoading: isLoadingRequestVersions } = useGetRequestVersions({
        path: {
            requestId,
            companyId,
        },
    });

    const sortedRequestVersions = useMemo(() => {
        const extendedRequestVersions = (requestVersions?.items || []).map((requestVersion, index) => ({
            ...requestVersion,
            localVersionNumber: index + 1,
        }));

        return arrayHelpers.arraySort(extendedRequestVersions, (u1, u2) =>
            compareHelpers.numberComparator2Desc(u1.version, u2.version)
        );
    }, [requestVersions]);

    useEffect(() => {
        const [activeRequestVersion] = sortedRequestVersions;

        if (activeRequestVersion) {
            setContentSplitView((contentSplitView) => ({
                ...contentSplitView,
                activeRequestVersion: activeRequestVersion.version,
            }));
        }
    }, [sortedRequestVersions, setContentSplitView]);

    useEffect(() => {
        setSplitViewFirstItemTitle(messages.firstPanelTitle);

        return () => {
            setSplitViewFirstItemTitle('');
        };
    }, [setSplitViewFirstItemTitle]);

    return (
        <Box spacing='12 60' height='100%'>
            {isLoadingRequestVersions && <Loading />}

            {!isLoadingRequestVersions && !sortedRequestVersions.length && <EmptyVersionHistoryList />}

            {sortedRequestVersions.map((requestVersion, index) => (
                <VersionHistoryItem
                    key={requestVersion.version}
                    active={index === 0}
                    checked={(selectedVersion?.version ?? sortedRequestVersions[0].version) === requestVersion.version}
                    requestVersion={requestVersion}
                    isFirst={index === 0}
                    isLast={index === sortedRequestVersions.length - 1}
                    isDextRequest={isDextRequest}
                    onSelectVersion={onSelectVersion}
                />
            ))}
        </Box>
    );
});

RequestVersionHistory.displayName = 'RequestVersionHistory';
